import React, { useState, useEffect } from 'react';
import { Button, Form, message, Input } from 'antd';
import { UserOutlined, LockOutlined, EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import axios from 'axios';
import CardComponent from '../components/Card';
import './AccountSettingsPage.css';



const AccountSettingsPage = ({ user, onLogout }) => {
  const [profileForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    // 폼 초기값 설정
    profileForm.setFieldsValue({
      username: user?.username || user?.name || '',
      email: user?.email || ''
    });
  }, [user, profileForm]);

  // 비밀번호 변경 처리
  const handlePasswordChange = async (values) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      const response = await axios.post('/api/auth/change-password', {
        currentPassword: values.current,
        newPassword: values.password
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        message.success('Password changed successfully');
        passwordForm.resetFields();
      } else {
        message.error(response.data.message || 'Failed to change password');
      }
    } catch (error) {
      if (error.response?.status === 401) {
        message.error('Current password is incorrect');
      } else if (error.response?.status === 400) {
        message.error(error.response.data.message || 'Invalid request');
      } else {
        message.error('Server error occurred. Please try again later');
      }
    } finally {
      setLoading(false);
    }
  };

  // 프로필 업데이트 처리
  const handleProfileUpdate = async (values) => {
    message.info('Profile update functionality will be implemented soon');
  };



  return (
    <div className="account-settings-page">
      <div className="page-header">
        <h1>Account Settings</h1>
        <p>Manage your account information and preferences</p>
      </div>

      <div className="settings-container">
        {/* Profile Section */}
        <section className="settings-section">
          <div className="section-header">
            <UserOutlined className="section-icon" />
            <h3>Profile Information</h3>
          </div>
          <CardComponent bgColor="bg-1">
            <Form
              form={profileForm}
              onFinish={handleProfileUpdate}
              layout="vertical"
              className="profile-form"
            >
              <div className="form-row">
                <Form.Item
                  name="username"
                  label="Username"
                  className="form-item"
                >
                  <Input 
                    placeholder="Username" 
                    disabled 
                    className="form-input"
                  />
                </Form.Item>
                <Form.Item
                  name="email"
                  label="Email"
                  className="form-item"
                >
                  <Input 
                    placeholder="Email" 
                    disabled 
                    className="form-input"
                  />
                </Form.Item>
              </div>

              <Form.Item>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  className="save-button"
                  disabled
                >
                  Save Profile
                </Button>
              </Form.Item>
            </Form>
          </CardComponent>
        </section>





        {/* Password Change Section */}
        <section className="settings-section">
          <div className="section-header">
            <LockOutlined className="section-icon" />
            <h3>Change Password</h3>
          </div>
          <CardComponent bgColor="bg-1">
            <Form
              form={passwordForm}
              onFinish={handlePasswordChange}
              layout="vertical"
              className="password-form"
            >
              <div className="form-row">
                <Form.Item
                  name="current"
                  label="Current Password"
                  rules={[
                    {
                      required: true,
                      message: 'Please input current password',
                    },
                  ]}
                  className="form-item"
                >
                  <Input.Password 
                    placeholder="Input current password" 
                    className="form-input"
                    iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                  />
                </Form.Item>
              </div>
              
              <div className="form-row">
                <Form.Item
                  name="password"
                  label="New Password"
                  rules={[
                    {
                      required: true,
                      message: 'Please input new password',
                    },
                    {
                      min: 8,
                      message: 'Password must be at least 8 characters',
                    },
                  ]}
                  className="form-item"
                >
                  <Input.Password 
                    placeholder="Input new password" 
                    className="form-input"
                    iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                  />
                </Form.Item>
                
                <Form.Item
                  name="confirm"
                  label="Confirm New Password"
                  dependencies={['password']}
                  rules={[
                    {
                      required: true,
                      message: 'Please confirm your password',
                    },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue('password') === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error('The two passwords do not match!'));
                      },
                    }),
                  ]}
                  className="form-item"
                >
                  <Input.Password 
                    placeholder="Confirm new password" 
                    className="form-input"
                    iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                  />
                </Form.Item>
              </div>

              <Form.Item>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  className="save-button save-password-button"
                  loading={loading}
                >
                  Save Password
                </Button>
              </Form.Item>
            </Form>
          </CardComponent>
        </section>
      </div>
    </div>
  );
};

export default AccountSettingsPage;
